import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  User,
  Activity,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type { IAuditLog } from "../types/quarters.types";

const AuditLogs = () => {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [logs, setLogs] = useState<IAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Filters & Paging
  const [filters, setFilters] = useState({
    action: "",
    page: 1,
    limit: 20,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await quartersService.getAuditLogs({
        ...filters,
        action: filters.action || undefined,
      });
      if (res.success) {
        setLogs(res.data);
        setTotalPages(res.totalPages || 1);
        setTotalCount(res.total || 0);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [filters, quartersService, toast]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  const toggleExpand = (id: string) => {
    setExpandedLogId(expandedLogId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <Activity className="w-8 h-8 text-blue-600" /> Module Audit Logs
          </h1>
        </div>
      </div>

      {/* Filter panel */}
      <div className="bg-white border border-slate-200 p-4 rounded-2xl mb-8 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase">Action Filter</label>
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value, page: 1 })}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
          >
            <option value="">All Actions</option>
            <option value="CREATE_BUILDING">Create Building</option>
            <option value="UPDATE_BUILDING">Update Building</option>
            <option value="DELETE_BUILDING">Delete Building</option>
            <option value="CREATE_FLOOR">Create Floor</option>
            <option value="UPDATE_FLOOR">Update Floor</option>
            <option value="DELETE_FLOOR">Delete Floor</option>
            <option value="CREATE_FLAT">Create Flat</option>
            <option value="UPDATE_FLAT">Update Flat</option>
            <option value="DELETE_FLAT">Delete Flat</option>
            <option value="UPDATE_WEIGHT">Update Scoring Weights</option>
            <option value="MANUAL_ALLOTMENT">Manual Allotment Override</option>
            <option value="AUTOMATIC_ALLOT_CONFIRM">Confirm Auto-Allotments</option>
            <option value="RUN_RANKING_ENGINE">Execute Ranks Update</option>
          </select>
        </div>
        <div className="md:col-span-2"></div>
      </div>

      {/* Audit table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-3xl">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-slate-500 text-sm font-semibold mt-3">Loading log files...</p>
        </div>
      ) : logs.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-slate-500 text-xs font-semibold uppercase">
                  <th className="px-6 py-4 text-left">Action</th>
                  <th className="px-6 py-4 text-left">Executing User</th>
                  <th className="px-6 py-4 text-left">IP Address</th>
                  <th className="px-6 py-4 text-left">Timestamp</th>
                  <th className="px-6 py-4 text-center">Data Modification</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                {logs.map((log) => (
                  <React.Fragment key={log._id}>
                    <tr className="hover:bg-slate-50/30 transition">
                      <td className="px-6 py-4 font-bold text-slate-800 text-xs tracking-wide">
                        <span className="bg-slate-100 text-slate-700 font-bold px-2.5 py-1 rounded-md border border-slate-200">
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="bg-slate-100 text-slate-600 p-1.5 rounded-full">
                            <User className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 text-xs">{log.userId?.name || "System"}</p>
                            <p className="text-[10px] text-slate-400">{log.userId?.email || ""}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-slate-500">{log.ipAddress || "::1"}</td>
                      <td className="px-6 py-4 text-xs text-slate-500">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleExpand(log._id)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-bold flex items-center justify-center gap-0.5 mx-auto transition"
                        >
                          {expandedLogId === log._id ? (
                            <>
                              Hide Diff <ChevronUp className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              View Diff <ChevronDown className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedLogId === log._id && (
                      <tr>
                        <td colSpan={5} className="bg-slate-50 p-6 border-b border-slate-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
                            <div className="space-y-1.5">
                              <p className="font-bold text-slate-500 uppercase tracking-wider">Before Value</p>
                              <pre className="bg-white border border-slate-200 p-4 rounded-xl overflow-x-auto font-mono text-[10px] text-slate-600 max-h-[250px] shadow-inner">
                                {log.oldValue ? JSON.stringify(log.oldValue, null, 2) : "NULL"}
                              </pre>
                            </div>
                            <div className="space-y-1.5">
                              <p className="font-bold text-slate-500 uppercase tracking-wider">After Value</p>
                              <pre className="bg-white border border-slate-200 p-4 rounded-xl overflow-x-auto font-mono text-[10px] text-slate-600 max-h-[250px] shadow-inner">
                                {log.newValue ? JSON.stringify(log.newValue, null, 2) : "NULL"}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center bg-slate-50/50 px-6 py-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Showing {logs.length} of {totalCount} logs
              </span>
              <div className="flex gap-2">
                <button
                  disabled={filters.page === 1}
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-xl transition disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={filters.page === totalPages}
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-xl transition disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
          <Activity className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-700">No Audit Logs</h2>
          <p className="text-slate-500 text-sm mt-1">Activity logs will populate as changes are executed.</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
